<script setup lang="ts">
import type { LocaleObject } from "@nuxtjs/i18n/dist/runtime/composables";

const { locales, locale } = useI18n();
const switchLocalePath = useSwitchLocalePath();

const { data: translations } = await useAsyncData(
  "translations",
  async () => {
    const res = await $fetch<any>(
      `/api/translations?lang=${locale.value}&mode=normal`,
    );
    return res;
  },
  { watch: [locale] },
);

const certificationKeys = ["certification-1", "certification-2"] as const;

const integratedManagementSystem = computed(
  () => translations.value?.["integrated-management-system"],
);

const availableLocales = computed(() =>
  locales.value.filter(
    (locale: string | LocaleObject) => (locale as LocaleObject).code !== locale,
  ),
);

const globalState = useGlobalState();
globalState.value.mode = "normal";

const isShow = ref<boolean>(true);
const handleShowChange = (newValue: boolean) => {
  isShow.value = newValue;
};

onMounted(() => {
  if (!import.meta.client) return;

  document.body.style.backgroundColor = "#ffffff";
  const contentMenu = document.querySelector(".content-menu") as HTMLElement;
  if (contentMenu) {
    contentMenu.style.background = "#ffffff";
  }
});

watchEffect(() => {
  if (process.client) {
    const htmlPage = document.querySelector("html") as HTMLHtmlElement;
    if (htmlPage && !isShow.value) {
      htmlPage.classList.add("page-scroll");
      (document.querySelector("body") as HTMLElement).style.overflow =
        "initial";
    }
  }
});
</script>

<template>
  <div class="normal-mode pdg-integrated-management-system-page">
    <HeadTags
      v-if="integratedManagementSystem?.seo"
      :title="integratedManagementSystem.seo.title"
      :description="integratedManagementSystem.seo.description"
      :url="integratedManagementSystem.seo.url"
      :type="integratedManagementSystem.seo.type"
      :keywords="integratedManagementSystem.seo.keywords"
    />
    <Loader @show-change="handleShowChange" />
    <Menu hide-menu />
    <section class="pdg-integrated-management-system-container pdg-container">
      <div class="row">
        <div class="col-md-12">
          <Chip
            :title="
              translations?.['integrated-management-system']?.['chip-title']
            "
            class="blue"
          />
          <h1
            class="pdg-title-h1 pdg-integrated-management-system-title"
            v-html="translations?.['integrated-management-system']?.title"
          ></h1>
          <div class="row">
            <div class="col-md-7">
              <p
                class="pdg-body-2 pdg-integrated-management-system-description"
                v-html="
                  translations?.['integrated-management-system'].description
                "
              ></p>
            </div>
            <div class="col-md-4 offset-md-1">
              <div class="pdg-integrated-management-system-subtitle-container">
                <h2
                  class="pdg-label-1 pdg-integrated-management-system-subtitle"
                >
                  {{ translations?.["integrated-management-system"].subtitle1 }}
                </h2>
                <p
                  class="pdg-body-2"
                  v-html="
                    translations?.['integrated-management-system'].description1
                  "
                ></p>
              </div>
              <div class="pdg-integrated-management-system-subtitle-container">
                <h2
                  class="pdg-label-1 pdg-integrated-management-system-subtitle"
                >
                  {{ translations?.["integrated-management-system"].subtitle2 }}
                </h2>
                <p
                  class="pdg-body-2"
                  v-html="
                    translations?.['integrated-management-system'].description2
                  "
                ></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      class="pdg-integrated-management-system-certifications-container pdg-container"
    >
      <div class="row">
        <div class="col-md-7">
          <Chip
            :title="
              translations?.['integrated-management-system'].certifications[
                'chip-title'
              ]
            "
            class="green"
          />
          <h2
            class="pdg-title-h1 pdg-integrated-management-system-certifications-title"
            v-html="
              translations?.['integrated-management-system'].certifications[
                'title'
              ]
            "
          ></h2>
        </div>
      </div>

      <div class="row pdg-integrated-management-system-certifications-grid">
        <article
          v-for="certKey in certificationKeys"
          :key="certKey"
          class="col-md-6"
        >
          <div class="pdg-card-certification">
            <span class="pdg-label-1 pdg-card-certification-type">
              {{
                translations?.["integrated-management-system"].certifications[
                  certKey
                ]["type"]
              }}
            </span>

            <div class="pdg-card-certification-badge">
              <img
                :src="
                  translations?.['integrated-management-system'].certifications[
                    certKey
                  ]['badge-src']
                "
                :alt="
                  translations?.['integrated-management-system'].certifications[
                    certKey
                  ]['badge-alt']
                "
              />
            </div>

            <h3 class="pdg-title-h4 pdg-card-certification-title">
              {{
                translations?.["integrated-management-system"].certifications[
                  certKey
                ]["title"]
              }}
            </h3>

            <p class="pdg-body-2 pdg-card-certification-description">
              {{
                translations?.["integrated-management-system"].certifications[
                  certKey
                ]["description"]
              }}
            </p>

            <div class="pdg-label-1 pdg-card-certification-meta">
              <span>
                {{
                  translations?.["integrated-management-system"].certifications[
                    certKey
                  ]["meta-entity"]
                }}
              </span>
              <span>
                {{
                  translations?.["integrated-management-system"].certifications[
                    certKey
                  ]["meta-number"]
                }}
              </span>
            </div>

            <div class="pdg-card-certification-ctas">
              <ArrowLink
                class="pdg-card-certification-learn-more pdg-button-small"
                :href="
                  translations?.['integrated-management-system'].certifications[
                    certKey
                  ]['learn-more-href']
                "
                :text="
                  translations?.['integrated-management-system'].certifications[
                    certKey
                  ]['learn-more-label']
                "
                :external="true"
                rel="noopener"
                :aria-label="
                  translations?.['integrated-management-system'].certifications[
                    'learn-more-aria-label'
                  ].replace(
                    '{standard}',
                    translations?.['integrated-management-system']
                      .certifications[certKey]['type'],
                  )
                "
              />
              <ButtonPrimary
                :to="
                  translations?.['integrated-management-system'].certifications[
                    certKey
                  ]['download-href']
                "
                :label="
                  translations?.['integrated-management-system'].certifications[
                    certKey
                  ]['download-label']
                "
                icon="icon-arrow-down"
                external
                :aria-label="
                  translations?.['integrated-management-system'].certifications[
                    'download-aria-label'
                  ].replace(
                    '{standard}',
                    translations?.['integrated-management-system']
                      .certifications[certKey]['type'],
                  )
                "
              />
            </div>
          </div>
        </article>
      </div>

      <section class="pdg-integrated-management-system-guideline">
        <div class="pdg-integrated-management-system-guideline-row">
          <span class="pdg-integrated-management-system-guideline-badge">
            <span class="iso">ISO/IEC</span>
            <span class="num">27017</span>
          </span>

          <div class="pdg-integrated-management-system-guideline-body">
            <span
              class="pdg-label-1 pdg-integrated-management-system-guideline-label"
            >
              {{
                translations?.["integrated-management-system"].guideline.label
              }}
            </span>
            <span
              class="pdg-subhead-1 pdg-integrated-management-system-guideline-title"
            >
              {{
                translations?.["integrated-management-system"].guideline.title
              }}
            </span>
            <p
              class="pdg-body-2 pdg-integrated-management-system-guideline-description"
            >
              {{
                translations?.["integrated-management-system"].guideline
                  .description
              }}
            </p>
          </div>

          <ButtonPrimary
            class="pdg-integrated-management-system-guideline-download"
            :to="
              translations?.['integrated-management-system'].guideline[
                'download-href'
              ]
            "
            :label="
              translations?.['integrated-management-system'].guideline[
                'download-label'
              ]
            "
            icon="icon-arrow-down"
            external
            :aria-label="
              translations?.['integrated-management-system'].guideline[
                'download-aria-label'
              ]
            "
          />
        </div>
      </section>
    </section>

    <div class="pdg-integrated-management-system-footer pdg-container">
      <footer class="pdg-integrated-management-system-footer-note">
        <div
          class="pdg-integrated-management-system-footer-note-content-container"
        >
          <div class="pdg-integrated-management-system-footer-note-content">
            <div
              class="pdg-integrated-management-system-footer-asterisks"
              aria-hidden="true"
            >
              <i class="icon-asterisk-01 pink"></i>
              <i class="icon-asterisk-01 light-blue"></i>
              <i class="icon-asterisk-01 dark-pink"></i>
              <i class="icon-asterisk-01 green"></i>
            </div>
            <p
              class="pdg-body-2 pdg-integrated-management-system-footer-text"
              v-html="
                translations?.['integrated-management-system']['footer-note']
                  .text
              "
            ></p>
          </div>
          <div class="pdg-company-info pdg-body-2 pdg-fade-in">
            <p class="pdg-paragraph-info">
              {{ translations?.footer?.companyInfo?.companyName }}
            </p>
            <address
              class="pdg-address-info"
              v-html="translations?.footer?.companyInfo?.address"
            ></address>
            <p>
              {{ translations?.footer?.companyInfo?.vatNumber }}
            </p>
            <a
              class="pdg-link-decoration pdg-animated-underline"
              href="mailto: info@paradigma.me"
            >
              info@paradigma.me
            </a>
          </div>
        </div>
        <div class="pdg-rights-languages pdg-fade-in row">
          <div class="col-md-3 order-md-3">
            <ul class="pdg-switch-language">
              <li
                class="pdg-language-list pdg-body-3"
                v-for="(localeObject, index) in availableLocales"
                :key="index"
              >
                <a
                  class="pdg-style-link pdg-animated-underline"
                  :class="{
                    'router-link-exact-active':
                      locale === (localeObject as LocaleObject).code,
                  }"
                  :href="switchLocalePath((localeObject as LocaleObject).code)"
                >
                  <span class="pdg-span-inline-block">
                    {{ (localeObject as LocaleObject).name }}
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <p
            class="pdg-label-2 pdg-margin-rights col-6 col-sm-3 col-md-4 order-md-1 offset-md-1"
          >
            {{ translations?.footer?.["right-reservered"]?.copyright }}
          </p>

          <p class="pdg-label-2 col-6 col-sm-3 col-md-3 order-md-2">
            {{ translations?.footer?.["right-reservered"]?.rightsReserved }}
          </p>
        </div>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../scss/includes.scss";

.pdg-integrated-management-system-page {
  color: var(--dark-blue);
  background-color: var(--white);
  min-height: 100vh;

  :deep(.content-menu) {
    background-color: var(--white) !important;
    color: var(--dark-blue);
  }

  :deep(.content-menu .pdg-link-logo) {
    opacity: 1;
  }

  .pdg-chip {
    margin-bottom: rem(16);
  }
}

.pdg-integrated-management-system-container,
.pdg-integrated-management-system-certifications-container {
  padding-top: rem(114);
  padding-bottom: rem(40);

  @media screen and (min-width: map-get($breakpoints, md)) {
    padding-top: vh(129);
    padding-bottom: vh(50);
  }

  .pdg-integrated-management-system-title {
    margin-bottom: rem(32);

    @media screen and (min-width: map-get($breakpoints, md)) {
      margin-bottom: vh(40);
    }
  }

  .pdg-integrated-management-system-subtitle {
    margin-bottom: rem(12);
    color: var(--blue);
  }

  .pdg-integrated-management-system-subtitle-container {
    margin-bottom: rem(24);

    &:last-child {
      margin-bottom: 0;
    }
  }

  .pdg-integrated-management-system-description {
    margin-bottom: rem(40);

    @media screen and (min-width: map-get($breakpoints, md)) {
      margin-bottom: 0;
    }
  }
}

.pdg-integrated-management-system-certifications-container {
  padding-top: rem(40);

  @media screen and (min-width: map-get($breakpoints, md)) {
    padding-top: vh(64);
  }

  .pdg-integrated-management-system-certifications-title {
    margin-top: rem(16);
    margin-bottom: rem(32);

    @media screen and (min-width: map-get($breakpoints, md)) {
      margin-bottom: vh(40);
    }
  }
}

.pdg-integrated-management-system-certifications-grid {
  row-gap: rem(24);
  margin-bottom: rem(24);

  @media screen and (min-width: map-get($breakpoints, md)) {
    margin-bottom: vh(32);
  }
}

.pdg-card-certification {
  display: flex;
  flex-direction: column;
  gap: rem(24);
  height: 100%;
  padding: rem(24);
  border: 1px solid rgba(10, 40, 67, 0.1);
  background: var(--white);
  transition: border-color 0.18s ease-in-out;

  @media screen and (min-width: map-get($breakpoints, md)) {
    padding: rem(32);
  }

  &:hover {
    border-color: var(--dark-blue);
  }

  .pdg-card-certification-type {
    color: var(--blue);
  }

  .pdg-card-certification-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    height: rem(200);
    background: var(--white);

    @media screen and (min-width: map-get($breakpoints, md)) {
      height: rem(240);
    }

    img {
      display: block;
      width: auto;
      height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
  }

  .pdg-card-certification-title {
    margin: 0;
    text-transform: none;
  }

  .pdg-card-certification-description {
    flex: 1;
    margin: 0;
  }

  .pdg-card-certification-meta {
    display: flex;
    flex-direction: column;
    gap: rem(4);
    color: var(--blue);
  }

  .pdg-card-certification-ctas {
    display: flex;
    flex-direction: column;
    gap: rem(14);
    margin-top: rem(8);
    padding-top: rem(24);
    border-top: 1px solid rgba(10, 40, 67, 0.1);

    @media screen and (min-width: map-get($breakpoints, md)) {
      flex-direction: row;
      justify-content: space-between;
    }
  }
}

.pdg-integrated-management-system-guideline {
  margin-bottom: rem(40);

  @media screen and (min-width: map-get($breakpoints, md)) {
    margin-bottom: vh(64);
  }
}

.pdg-integrated-management-system-guideline-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: rem(20);
  align-items: center;
  padding: rem(20) rem(24);
  border: 1px solid rgba(10, 40, 67, 0.1);
  border-left: rem(4) solid var(--dark-blue);
  background: var(--white);

  @media screen and (min-width: map-get($breakpoints, md)) {
    grid-template-columns: auto 1fr auto;
    gap: rem(32);
    padding: rem(24) rem(28);
  }
}

.pdg-integrated-management-system-guideline-badge {
  display: inline-flex;
  align-items: baseline;
  gap: rem(8);
  padding: rem(6) rem(14);
  background: var(--dark-blue);
  white-space: nowrap;

  .iso {
    @include typography(11, 500, 11, $text-transform: uppercase);
    letter-spacing: 0.08em;
    color: var(--white);
  }

  .num {
    @include typography(18, 500, 18);
    color: var(--green);
    letter-spacing: -0.01em;
  }
}

.pdg-integrated-management-system-guideline-body {
  display: flex;
  flex-direction: column;
  gap: rem(4);
}

.pdg-integrated-management-system-guideline-label {
  color: var(--dark-pink);
}

.pdg-integrated-management-system-guideline-title {
  color: var(--dark-blue);
}

.pdg-integrated-management-system-guideline-description {
  margin: rem(4) 0 0;
}

.pdg-integrated-management-system-footer {
  padding-bottom: rem(40);

  @media screen and (min-width: map-get($breakpoints, md)) {
    padding-bottom: vh(80);
  }
}

.pdg-integrated-management-system-footer-note {
  // display: grid;
  // grid-template-columns: 1fr;
  gap: rem(24);
  align-items: flex-end;
  padding-top: rem(40);
  border-top: 1px solid rgba(10, 40, 67, 0.06);

  @media screen and (min-width: map-get($breakpoints, md)) {
    // grid-template-columns: auto 1fr auto;
    padding-top: vh(56);
  }
}

.pdg-integrated-management-system-footer-note-content-container {
  display: flex;
  flex-direction: column;
  gap: rem(32);

  @media screen and (min-width: map-get($breakpoints, md)) {
    flex-direction: row;
    justify-content: space-between;
  }
}

.pdg-integrated-management-system-footer-asterisks {
  display: flex;
  gap: rem(12);

  i {
    @include typography(22, 400, 22);

    &.pink {
      color: var(--pink);
    }

    &.light-blue {
      color: var(--light-blue);
    }

    &.dark-pink {
      color: var(--dark-pink);
    }

    &.green {
      color: var(--green);
    }
  }
}

.pdg-integrated-management-system-footer-text {
  max-width: rem(540);
  margin: 0;
}

.pdg-integrated-management-system-footer-company {
  margin: 0;
  color: var(--dark-blue);
  text-align: left;

  @media screen and (min-width: map-get($breakpoints, md)) {
    text-align: right;
  }
}

.pdg-company-info {
  .pdg-paragraph-info {
    font-weight: 500;
    margin-bottom: 0;
  }

  .pdg-address-info {
    margin-bottom: 0;
  }

  .pdg-link-decoration {
    text-decoration: none;
    color: currentColor;
  }
}

.pdg-integrated-management-system-footer-note-content {
  display: flex;
  flex-direction: column;
  gap: rem(24);

  @media screen and (min-width: map-get($breakpoints, md)) {
    flex-direction: row;
  }
}

.pdg-integrated-management-system-footer .pdg-rights-languages {
  padding-top: rem(48);

  @media screen and (min-width: map-get($breakpoints, md)) {
    align-items: center;
  }

  .pdg-label-2 {
    margin-bottom: 0;
    color: var(--dark-blue);
  }

  .pdg-switch-language {
    display: flex;
    padding-bottom: rem(32);
    margin-bottom: 0;
    padding-left: 0;

    @media screen and (min-width: map-get($breakpoints, md)) {
      padding-bottom: 0;
    }

    .pdg-language-list {
      list-style: none;

      &:first-child::after {
        content: "-";
        padding: 0 rem(16);
        color: var(--dark-blue);
      }

      .pdg-span-inline-block {
        display: inline-block;
      }
    }

    .pdg-style-link {
      text-decoration: none;
      color: var(--dark-blue);

      &:hover,
      &:focus,
      &:focus-visible,
      &.router-link-exact-active {
        &::after {
          width: 100%;
        }
      }

      &.router-link-exact-active {
        color: var(--dark-pink);
        transition: transform 0.3s ease-in-out;
      }
    }
  }
}
</style>
